import { useCallback } from 'react';
import { isDesktop } from '@/lib/fileSystem';
import { useAuth } from '@/hooks/useAuth';
import { getCloudState } from '@/lib/cloudUpsell';
import { useFileDialogContext } from '@/contexts/FileDialogContext';

/**
 * Unified file dialog hook — picks a source based on platform + cloud state:
 *
 *   Desktop (Electron) → native OS picker (unchanged; file association,
 *                        "Recently opened" from OS, etc. all still work).
 *   Web + cloud        → Scrivar modal with Device / Cloud tabs.
 *   Web + no cloud     → native `<input type=file>` (unchanged; zero-friction
 *                        for the free path).
 *
 * Result shape stays compatible: `{ name, buffer, path, cloudDocId? }`.
 * Pages that care about cloud provenance can read `cloudDocId`; pages that
 * don't (most of them) see the same keys they always did.
 *
 *   const { openFile, openFiles, saveFile, openFolder, desktop } = useFileDialog();
 *   const result = await openFile('.pdf');        // { name, buffer, path, cloudDocId? } or null
 */
export function useFileDialog() {
  const desktop = isDesktop();
  const { user, account } = useAuth();
  const ctx = useFileDialogContext();

  // Cloud modal only lights up when the provider is mounted AND the user has
  // cloud enabled. Anything else falls through to the native picker so the
  // Free / Lite path doesn't pay a modal tax. The `accept` check keeps
  // non-PDF pickers (Batch image-to-PDF, etc.) on the native path — cloud
  // stores only PDFs, so a modal would show no useful content anyway.
  const cloudAvailable = !desktop && ctx && getCloudState(user, account) === 'available';
  const acceptAllowsCloud = (accept) => typeof accept === 'string' && accept.toLowerCase().includes('.pdf');

  const openFile = useCallback(async (accept = '.pdf', opts = {}) => {
    if (cloudAvailable && acceptAllowsCloud(accept)) {
      const picked = await ctx.requestFileOpen(accept, {
        multiple: false,
        initialTab: opts.preferCloud ? 'cloud' : null,
      });
      if (!picked) return null;
      return picked;
    }
    if (desktop) {
      const result = await window.electronAPI.openFile(accept);
      if (!result) return null;
      return { name: result.name, buffer: result.buffer, path: result.path };
    }
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.oncancel = () => resolve(null);
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return resolve(null);
        try {
          const buffer = await file.arrayBuffer();
          resolve({ name: file.name, buffer, path: null });
        } catch (err) { reject(err); }
      };
      input.click();
    });
  }, [desktop, cloudAvailable, ctx]);

  const openFiles = useCallback(async (accept = '.pdf', opts = {}) => {
    if (cloudAvailable && acceptAllowsCloud(accept)) {
      const picked = await ctx.requestFileOpen(accept, {
        multiple: true,
        initialTab: opts.preferCloud ? 'cloud' : null,
      });
      if (!picked) return [];
      // Modal returns an array when multiple=true, single object otherwise —
      // normalize here so callers always get an array from openFiles().
      return Array.isArray(picked) ? picked : [picked];
    }
    if (desktop) {
      const results = await window.electronAPI.openFiles(accept, { conversionMetadata: opts.conversionMetadata === true });
      if (!results || results.length === 0) return [];
      return results;
    }
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = true;
      input.oncancel = () => resolve([]);
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return resolve([]);
        try {
        const results = await Promise.all(
          files.map(async (f) => ({
            name: f.name,
            buffer: await f.arrayBuffer(),
            path: null,
          }))
        );
        resolve(results);
        } catch (err) { reject(err); }
      };
      input.click();
    });
  }, [desktop, cloudAvailable, ctx]);

  const saveFile = useCallback(async (data, suggestedName) => {
    if (desktop) {
      const bytes = data instanceof Blob
        ? new Uint8Array(await data.arrayBuffer())
        : data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : data;
      return window.electronAPI.saveFile(bytes, suggestedName);
    }
    const blob = data instanceof Blob ? data : new Blob([data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return suggestedName;
  }, [desktop]);

  const openFolder = useCallback(async () => {
    if (desktop) {
      return window.electronAPI.openFolder();
    }
    return null;
  }, [desktop]);

  return { openFile, openFiles, saveFile, openFolder, desktop, cloudAvailable };
}
